// Клиент `/api/v1/settings/repository-settings/{owner}/{repo}`.
//
// Контракт backend'а — pydantic-схемы
// `backend/modules/settings/schemas.py`. Имена полей берём 1:1.
// GET отдаёт текущие значения строки (или заводские дефолты при
// `has_row=false`) + блок `defaults` (что значит «наследует»). PUT
// принимает все настраиваемые поля; nullable-override `null` =
// «наследовать глобальный дефолт». JWT прикручивает `authedFetch`.
// Значение вне диапазона → 422 с человекочитаемым `detail`.

import { readErrorDetail } from "../../api/errorDetail";
import { authedFetch } from "../../auth/authedFetch";

// HTTP-код «значение не прошло серверную валидацию» (тело с `detail`).
export const HTTP_UNPROCESSABLE_ENTITY = 422;

const BASE = "/api/v1/settings/repository-settings";

// Заводские/глобальные дефолты — UI показывает их как «наследуемое»
// значение (NOT NULL-поля при отсутствии строки, nullable при null).
// `max_retries` — эффективный бюджет организации: что даст наследование.
export interface RepositorySettingsDefaults {
  readonly max_retries: number;
  readonly reaper_awaiting_stale_seconds: number;
  readonly reconcile_enabled: boolean;
  readonly reconcile_interval_seconds: number;
  readonly probe_period_seconds: number;
  readonly probe_failure_threshold: number;
  readonly probe_success_threshold: number;
  readonly probe_timeout_seconds: number;
  readonly probe_initial_delay_seconds: number;
  readonly executor_cooldown_seconds: number;
  readonly migration_path_globs: readonly string[];
}

// Ответ GET/PUT: текущее значение всех полей + дефолты + has_row.
// `max_retries` — переопределение репозитория (`null` — как у организации),
// `effective_max_retries` — бюджет, который получит задача.
export interface RepositorySettingsResponse {
  readonly repository_full_name: string;
  readonly has_row: boolean;
  readonly max_retries: number | null;
  readonly effective_max_retries: number;
  readonly reaper_awaiting_stale_seconds: number;
  readonly reconcile_enabled: boolean;
  readonly reconcile_interval_seconds: number;
  readonly probe_period_seconds: number | null;
  readonly probe_failure_threshold: number | null;
  readonly probe_success_threshold: number | null;
  readonly probe_timeout_seconds: number | null;
  readonly probe_initial_delay_seconds: number | null;
  readonly executor_cooldown_seconds: number | null;
  readonly migration_path_globs: readonly string[] | null;
  readonly defaults: RepositorySettingsDefaults;
}

// Body PUT: все настраиваемые поля (nullable `null` = наследовать).
export interface UpdateRepositorySettingsRequest {
  readonly max_retries: number | null;
  readonly reaper_awaiting_stale_seconds: number;
  readonly reconcile_enabled: boolean;
  readonly reconcile_interval_seconds: number;
  readonly probe_period_seconds: number | null;
  readonly probe_failure_threshold: number | null;
  readonly probe_success_threshold: number | null;
  readonly probe_timeout_seconds: number | null;
  readonly probe_initial_delay_seconds: number | null;
  readonly executor_cooldown_seconds: number | null;
  readonly migration_path_globs: readonly string[] | null;
}

/**
 * HTTP-ошибка с числовым `status`. Секция различает 422 (значение вне
 * диапазона, `detail` в теле) от прочих кодов. Зеркало
 * `branchNameTemplate.HttpError`.
 */
export class HttpError extends Error {
  public readonly status: number;
  public readonly detail: string | null;
  public constructor(status: number, message: string, detail: string | null) {
    super(message);
    this.name = "HttpError";
    this.status = status;
    this.detail = detail;
  }
}

function buildHeaders(): Record<string, string> {
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
}

// Бросить HttpError с `detail` тела отказа FastAPI.
async function throwHttp(response: Response, label: string): Promise<never> {
  const detail = await readErrorDetail(response);
  throw new HttpError(
    response.status,
    `${label}: HTTP ${String(response.status)}`,
    detail,
  );
}

function repoPath(repoFullName: string): string {
  // `owner/repo` уходит в путь как есть — оба сегмента уже URL-безопасны
  // (имена репозиториев Forgejo не содержат `/` внутри сегмента);
  // раздельное кодирование сломало бы слеш-разделитель. Паттерн
  // `branchNameTemplate.repoPath`.
  return `${BASE}/${repoFullName}`;
}

export async function fetchRepositorySettings(
  repoFullName: string,
): Promise<RepositorySettingsResponse> {
  const response = await authedFetch(repoPath(repoFullName), {
    headers: buildHeaders(),
  });
  if (!response.ok) {
    await throwHttp(response, "fetchRepositorySettings");
  }
  return (await response.json()) as RepositorySettingsResponse;
}

export async function updateRepositorySettings(
  repoFullName: string,
  body: UpdateRepositorySettingsRequest,
): Promise<RepositorySettingsResponse> {
  const response = await authedFetch(repoPath(repoFullName), {
    method: "PUT",
    headers: buildHeaders(),
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    await throwHttp(response, "updateRepositorySettings");
  }
  return (await response.json()) as RepositorySettingsResponse;
}
